// #3293 - the pure half of the equitation / medal section grid: how a class's
// rides group into sections, and what a drag (or a keyboard lift) means in
// terms of score-common's planSubsectionChanges input. No React, no IO.
import { isSectionTestId } from "score-common";
import { isRideEnded } from "./endRide";
import { planRideSubsections } from "../local/rideSubsections";

// Droppable ids shared by SectionBar, the rows and the drop handler.
export const TAB_PREFIX = "tab:";
export const ROW_PREFIX = "row:";
export const NEW_SECTION_ID = `${TAB_PREFIX}new`;
export const POOL_ID = `${TAB_PREFIX}pool`;
export const sectionTabId = (n) => `${TAB_PREFIX}${n}`;
export const rowDropId = (rideId) => `${ROW_PREFIX}${rideId}`;
// "Drop after the last row" (keyboard lift only: a pointer drops on a row).
export const ROW_END_ID = `${ROW_PREFIX}__end`;

/** Sections exist for equitation / medal rides only, gated on the TEST ID. */
export function isSectionClass(rides) {
  return Boolean(rides?.length) && isSectionTestId(rides[0]?.testId);
}

const byOrder = (a, b) => {
  const ao = a.subsectionOrder ?? Infinity;
  const bo = b.subsectionOrder ?? Infinity;
  if (ao !== bo) return ao < bo ? -1 : 1;
  return (a.rideNumber ?? 0) - (b.rideNumber ?? 0);
};

const inSection = (r) => Number.isInteger(r.subsection) && r.subsection > 0;

/**
 * Group a class's rides.
 *  - sections: [{ number, rides }] ascending, each ride list in subsectionOrder
 *  - pool: not drawn and still to ride (the "Not drawn (N)" tab)
 *  - ended: already No show / Scratched / etc. (listed in the
 *    pool tab, but they never block the finish)
 * An ended rider is never a row in a section grid, whatever its subsection.
 */
export function groupRidesBySection(rides) {
  const bySection = new Map();
  const pool = [];
  const ended = [];
  for (const r of rides) {
    if (isRideEnded(r)) {
      ended.push(r);
    } else if (inSection(r)) {
      if (!bySection.has(r.subsection)) bySection.set(r.subsection, []);
      bySection.get(r.subsection).push(r);
    } else {
      pool.push(r);
    }
  }
  const sections = [...bySection.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([number, list]) => ({ number, rides: [...list].sort(byOrder) }));
  const rn = (a, b) => (a.rideNumber ?? 0) - (b.rideNumber ?? 0);
  return { sections, pool: pool.sort(rn), ended: ended.sort(rn) };
}

const planInput = (rides) =>
  rides.map((r) => ({
    id: r.id,
    rideNumber: r.rideNumber,
    subsection: r.subsection ?? null,
    subsectionOrder: r.subsectionOrder ?? null,
  }));

/** The next free section number (the "+ New section" target). */
export function nextSectionNumber(rides) {
  const nums = rides.filter(inSection).map((r) => r.subsection);
  return nums.length ? Math.max(...nums) + 1 : 1;
}

/**
 * Move riders to a section. `target`: a section number, "new" (the next
 * section), or null (back to Not drawn). Riders land at the end, in the
 * order given. Returns the payload rows ({ rideId, subsection, subsectionOrder }).
 */
export function planMove(rides, rideIds, target) {
  const subsection = target === "new" ? nextSectionNumber(rides) : target;
  const changes = rideIds.map((rideId) => ({ rideId, subsection }));
  return planRideSubsections(planInput(rides), changes);
}

/**
 * Reorder inside a section: `rideId` lands BEFORE `beforeRideId` (null = the
 * end). Both must be in the same section; a different section is ignored.
 */
export function planReorder(rides, rideId, beforeRideId) {
  const moving = rides.find((r) => r.id === rideId);
  if (!moving || !inSection(moving)) return [];
  const section = groupRidesBySection(rides).sections.find((s) => s.number === moving.subsection);
  if (!section) return [];
  const ids = section.rides.map((r) => r.id).filter((id) => id !== rideId);
  const at = beforeRideId ? ids.indexOf(beforeRideId) : -1;
  if (beforeRideId && at < 0) return [];
  ids.splice(at < 0 ? ids.length : at, 0, rideId);
  const changes = ids.map((id, i) => ({
    rideId: id,
    subsection: moving.subsection,
    subsectionOrder: i,
  }));
  return planRideSubsections(planInput(rides), changes);
}

/**
 * What a drop means. `activeId` is the dragged ride's id, `overId` a droppable
 * id from this file. Returns { kind, rows } or null when it is a no-op.
 */
export function resolveDrop(rides, activeId, overId) {
  if (!activeId || !overId) return null;
  const active = rides.find((r) => r.id === activeId);
  if (!active) return null;
  let rows = [];
  let kind;
  if (overId === NEW_SECTION_ID) {
    kind = "new";
    rows = planMove(rides, [activeId], "new");
  } else if (overId === POOL_ID) {
    kind = "pool";
    if (!inSection(active)) return null;
    rows = planMove(rides, [activeId], null);
  } else if (overId.startsWith(TAB_PREFIX)) {
    const n = Number(overId.slice(TAB_PREFIX.length));
    if (!Number.isInteger(n) || n <= 0 || active.subsection === n) return null;
    kind = "section";
    rows = planMove(rides, [activeId], n);
  } else if (overId === ROW_END_ID) {
    kind = "reorder";
    rows = planReorder(rides, activeId, null);
  } else if (overId.startsWith(ROW_PREFIX)) {
    const targetId = overId.slice(ROW_PREFIX.length);
    if (targetId === activeId) return null;
    const target = rides.find((r) => r.id === targetId);
    if (!target || !inSection(target) || target.subsection !== active.subsection) return null;
    kind = "reorder";
    rows = planReorder(rides, activeId, targetId);
  } else {
    return null;
  }
  return rows.length ? { kind, rows } : null;
}

/** Apply payload rows to a ride list (used for the optimistic local view). */
export function applyRows(rides, rows) {
  const byId = new Map(rows.map((r) => [r.rideId, r]));
  return rides.map((r) => {
    const hit = byId.get(r.id);
    return hit ? { ...r, subsection: hit.subsection, subsectionOrder: hit.subsectionOrder } : r;
  });
}
